import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import TeamMatchupImage from '@mlb/react-patterns/v4/components/TeamMatchupImage/teamMatchupImage';
import {
    MobileStoryLink,
    StoryOverlayWrapper,
    TapStoryContainer,
    TapStoryIFrame,
    TapStoryInfo,
    TapStoryText
} from './TapStory.style';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import Icon from '@mlb/react-patterns/v4/components/Icon';
import COLORS from '@mlb/react-patterns/v4/style/universal.color';
import GDBREAKPOINTS from '../themes/breakpoints';
import { useMedia } from '@mlb/react-patterns/v4/utils/hooks';
import useTracking from '@gameday/utils/hooks/useTracking';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import FallbackImage from './components/FallbackImage';

const THREE_X = '3x';
const TWO_X = '2x';
const ONE_X = '1x';

const MAX_WIDTH = 1056;
const srcSetWidth = {
    [THREE_X]: MAX_WIDTH * 3,
    [TWO_X]: MAX_WIDTH * 2,
    [ONE_X]: MAX_WIDTH
};

const { mlbWhite } = COLORS;

const { XS_MAX } = GDBREAKPOINTS;

const cfg = {
    componentName: ['Tap Story'],
    text: 'Game Story',
    activity: 'Cover Image Link Click',
    linkName: 'Tap Story'
};

const embedParams = [
    ['landscape', true],
    ['suppressPoll', true],
    ['suppressOutlink', true],
    ['suppressOutlinkSponsor', true],
    ['suppressDrawer', true],
    ['ap', 1]
];

const TapStory = ({
    awayTeamId,
    homeTeamId,
    storyUrl,
    validGameday,
    teamId
}) => {
    const isSingleColumnNarrow = useMedia({ maxWidth: XS_MAX });

    const formattedStoryUrl = useMemo(() => {
        if (!storyUrl) return '';

        const url = new URL(`https://${storyUrl}`);

        if (teamId) {
            url.searchParams.set('teamId', teamId);
        }

        if (isSingleColumnNarrow) {
            return url.toString();
        }

        url.hash = 'embedMode=2';

        embedParams.forEach(([key, value]) => {
            url.searchParams.set(key, value);
        });

        return url.toString();
    }, [storyUrl, teamId, isSingleColumnNarrow]);

    const translate = useTranslate();
    const gameStoryText = translate('button_game_story', 'Game Story');
    const [isError, setIsError] = useState(false);
    const isLoading = typeof storyUrl === 'undefined';

    const Tracking = useTracking();
    const handleTapStoryUrl = useCallback(() => {
        Tracking.track.track_click(cfg);
    }, [formattedStoryUrl]);

    const handlers = useMemo(
        () => ({
            onClick: handleTapStoryUrl
        }),
        [handleTapStoryUrl]
    );

    useEffect(() => {
        setIsError(false);
        const handleMessage = (event) => {
            if (event?.data?.app === 'x-stories' && event?.data?.type === 'server-error') {
                console.error('TAP STORY ERROR: ', event?.data?.data);
                setIsError(true);
            }
        };

        window.addEventListener('message', handleMessage, false);

        return () => {
            window.removeEventListener('message', handleMessage, false);
        };
    }, [storyUrl]);

    const getContent = () => {
        if (isLoading) {
            return <Skeleton width="100%" height="100%" />;
        }

        if (!validGameday || isError || !storyUrl) {
            return (
                <FallbackImage
                    awayTeamId={awayTeamId}
                    homeTeamId={homeTeamId}
                    srcSetWidth={srcSetWidth}
                    srcWidth={srcSetWidth[THREE_X]}
                />
            );
        }

        if (isSingleColumnNarrow) {
            return (
                <StoryOverlayWrapper>
                    <MobileStoryLink
                        href={formattedStoryUrl}
                        target="_blank"
                        ariaLabel={gameStoryText}
                        handlers={handlers}
                    />
                    <Icon name="icon-gamestory" fill={mlbWhite} width="28" />
                    <TeamMatchupImage
                        awayTeamId={awayTeamId}
                        homeTeamId={homeTeamId}
                        ratio="16:9"
                        srcsetWidth={srcSetWidth}
                        srcWidth={srcSetWidth[THREE_X]}
                    />
                    <TapStoryInfo>
                        <TapStoryText>
                            <div>{gameStoryText}</div>
                        </TapStoryText>
                    </TapStoryInfo>
                </StoryOverlayWrapper>
            );
        }

        return (
            <TapStoryIFrame
                src={formattedStoryUrl}
                allow="autoplay"
                sandbox="allow-top-navigation-by-user-activation allow-popups-to-escape-sandbox allow-forms allow-modals allow-pointer-lock allow-popups allow-same-origin allow-scripts allow-top-navigation"
                aria-label={gameStoryText}
            />
        );
    };

    return (
        <TapStoryContainer>
            {getContent()}
        </TapStoryContainer>
    );
};

TapStory.propTypes = {
    awayTeamId: PropTypes.number,
    homeTeamId: PropTypes.number,
    storyUrl: PropTypes.string,
    validGameday: PropTypes.bool,
    teamId: PropTypes.number
};

export default withTheme(memo(TapStory));
